// src/components/crm/QuoteFormModal.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, setDoc, addDoc, serverTimestamp, collection, query, orderBy, onSnapshot, deleteDoc, deleteField } from 'firebase/firestore'; // taxlines2-3e0b16a4 — deleteField drops stale tax parts
import { dropStaleTaxParts } from '../../utils/taxPartsOnSave'; // taxlines2-3e0b16a4
import { httpsCallable } from 'firebase/functions';
import { X, Trash2, Save, Wrench, Package, Search, Loader2, AlertCircle, FileText, Lock, Star, Plus, Link2, Wallet, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { parseLocalDate, formatCurrency, formatLocalDate, stripFirestoreSentinels } from '../../utils/formatters'; // sentstrip-2e8c5d47 localstamp-8d2c5b39
import { buildAddressString } from '../../utils/address'; // PROPERTIES v3
import { buildBillToFromProperty, buildBillToFromCustomer, normalizeBillTo } from '../../utils/billTo'; // billto-qfm-import-1c6e9a35
import CustomerCombobox from './CustomerCombobox'; // custpick-8e5a2c71
// r1-2c-qfm-notesstrip-a4c8f2d5 — reuse the QuoteDetailsView Notes & Media pieces (used, not modified)
import useOrgNotes from '../../hooks/useOrgNotes';
import NoteComposer from './NoteComposer';
import NoteCard from './NoteCard';
import ConfirmationModal from '../common/ConfirmationModal'; // seamF2-import-b4d1e9a7 — reused confirm gate for optional-deposit approval signal
// PHASE 3 VERIFIER: r1-p3-qfm-imports-8d4a2e91
// Selective-approval feature: per-line selectionType + parentItemId
// + stable itemId. SELECTION_TYPES, DEFAULT_SELECTION_TYPE,
// generateItemId come from the shared schema module.
import { SELECTION_TYPES, DEFAULT_SELECTION_TYPE, generateItemId, DOC_TYPES, resolveDocTerminology, resolveCreateSalesPersonId, resolveQuoteValidityDays } from '../../utils/quoteSchema'; // r1-p1-docterm-qfm-import-3b7e1f04 qvalid-7c2e4a91
import { pickerItems } from '../../utils/catalogUi'; // catscreen-7a2c4e91 — pickers hide archived items
import * as lineIds from '../../utils/lineIds'; // lineidweb-9e4a2d17 — permanent per-line IDs + the one template function
// SEAM 6 VERIFIER: r1-dep-qfm-import-9e4c1a73
// Deposits Seam 6 — operator deposit config. DEPOSIT_TYPES/isValidDepositType
// validate the picker, resolveDepositAmount powers the live preview (same math
// the server re-runs at approval), resolveDepositRequired applies the
// per-estimate ?? org-default fallback chain.
import { DEPOSIT_TYPES, isValidDepositType, resolveDepositAmount, resolveDepositRequired, resolveDepositDefaults } from '../../utils/quoteSchema';
// SEAM 1a VERIFIER: qfm-orgpay-import-a1b2c3d4
// Client-side Stripe-onboarding gate. isOrgStripeReady mirrors the server
// deposit gate exactly (see src/utils/orgPayments.js) so an un-onboarded org
// can't AUTHOR a deposit requirement it could never actually collect.
import { canOrgTakeOnlinePayments } from '../../utils/orgPayments'; // payhide-7e4c1b05 -- was isOrgStripeReady, which missed a restricted Stripe account
import LineDescriptionInput from '../documents/LineDescriptionInput'; // linetext-4d8b2e61 — Enter, indents and "- " bullets in line descriptions

// r1-seam0c-creditline-5d1a8e60 — orgUsers is the org's user list, passed from
// CrmApp (which already holds it and hands it to other modals as orgUsers). It
// exists solely to resolve the CREDITED rep's display name by uid. Deliberately
// not read from customer.sales_person_name: that denormalized field is only
// written at customer-create and by the reassignCustomer callable, so the
// profile's rep dropdown can change the rep without refreshing it. Defaulted to
// [] so any other mount site keeps working (the line just doesn't render).
const QuoteFormModal = ({ closeModal, editingQuote, currentUserProfile, customers, services, companySettings, orgUsers = [] }) => {
    // localstamp-8d2c5b39 — these seeds were already fresh per-open (declared in the
    // component), but they ran through toISOString() = UTC, so opening the app after
    // ~8pm Eastern stamped tomorrow. formatLocalDate keeps them on the LOCAL calendar day.
    const today = formatLocalDate();
    const expiryDate = new Date();
    // qvalid-qfm-seed-4f9b2d73 — Seam 2: validity length from the org setting
    // (quoteDefaults.validityDays; falls back to the old 30). Seeds NEW documents only.
    expiryDate.setDate(expiryDate.getDate() + resolveQuoteValidityDays(companySettings?.quoteDefaults));
    const defaultExpiry = formatLocalDate(expiryDate);

    const quoteStatuses = ['Draft', 'Sent', 'Approved', 'Declined'];
    // r1-2c-qfm-path-1a2b3c4d — ONE org/path source. The pre-allocation effect
    // and the create write both read these, so two hand-built paths can never
    // disagree. Optional chaining: undefined orgId hides the strip + no-ops save.
    const orgId = currentUserProfile?.organizationId;
    const quotesPath = `organizations/${orgId}/quotes`;

    // --- State for Tax Options ---
    const [taxOptions, setTaxOptions] = useState([]);
    const [isLoadingTax, setIsLoadingTax] = useState(false);

    // --- Default Line Item ---
    // PHASE 3 VERIFIER: r1-p3-qfm-default-2c8b5e34
    // Note: itemId is NOT in the default — it's minted fresh per new
    // line via makeNewLineItem() below. selectionType defaults to
    // 'required' to match pre-feature behavior. parentItemId stays
    // null unless the operator explicitly chooses 'included'.
    const defaultLineItem = {
        serviceId: '',
        name: '',
        description: '',
        quantity: 1,
        price: 0,
        taxExempt: false,
        taxExemptionNumber: '',
        type: 'Service',       
        taxCodeId: '',         
        addToCatalog: false,
        selectionType: DEFAULT_SELECTION_TYPE,  // 'required'
        parentItemId: null,
    };

    // Mint a new line item with a fresh itemId UUID. Used by
    // addLineItem and the initial-state path.
    const makeNewLineItem = () => lineIds.newLine({ ...defaultLineItem, itemId: generateItemId() }, lineIds.newLineId); // lineidweb-9e4a2d17 — + its own lineId

    const [quoteData, setQuoteData] = useState({
        customerId: '',
        customerName: '',
        customerDetails: {},
        // PROPERTIES v3 — service location (separate from the bill-to customerDetails).
        propertyId: null,
        propertyName: null,
        serviceAddress: null,
        billTo: null, // billto-qfm-state-8d2b7f43 -- null/absent = bill the main customer (today's behaviour)
        title: 'Quote',
        description: '',
        createdAt: today,
        expiresAt: defaultExpiry,
        lineItems: [makeNewLineItem()],
        notes: '',
        status: 'Draft',
        // PHASE FU-QC VERIFIER: r1-fu-qc-5b2d8c41
        // estimatedDuration (minutes) persists on the quote so Convert to Job
        // can pre-populate the job's duration. Set from template at applyTemplate
        // time. Mirrors mobile QuoteBuilderScreen. Existing quotes without this
        // field load with default 0 (Convert falls back to line-item-derived or
        // 60 min default).
        estimatedDuration: 0,
    });
    // PROPERTIES v3 — service-location dropdown value: '' unchosen | 'main' | property id.
    const [serviceLocation, setServiceLocation] = useState('');
    // billto-qfm-selstate-4a9c1e76 -- Bill To selector state. pendingThirdParty covers the
    // moment between choosing "Another customer..." and picking which one; billToTouched
    // stops the per-property default from overriding a manual choice. It initializes TRUE
    // when the loaded document already carries a deliberate bill-to, so editing a saved
    // quote and then switching the service location can never silently replace that
    // choice with the property default (plan section 2.4 survival guarantee).
    const [pendingThirdParty, setPendingThirdParty] = useState(false);
    const [billToTouched, setBillToTouched] = useState(() => !!normalizeBillTo(editingQuote?.billTo));
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    // r1-2c-qfm-prealloc-1f7d3a80 — mirror NoteComposer.ensureNoteId: for a NEW
    // quote, mint the quote doc ref so the Notes & Media strip can attach photos
    // before first save; the create branch writes to this exact ref via setDoc.
    // DEP is [editingQuote?.id, orgId] — a STRING compared by value, NOT the
    // editingQuote OBJECT: a parent re-render passing a new object identity must
    // not re-run this and mint a SECOND id mid-session (which would instantly
    // strand the first id's note and reset the strip count to 0). The
    // `if (newQuoteRefRef.current) return` guard makes minting idempotent within
    // a new-quote session; edit->new still re-syncs because the edit branch
    // clears the ref first, so the guard passes on the following new run (corr. 1c).
    const newQuoteRefRef = useRef(null);
    // r1-2d-qfm-noteref-7b3e9d02 — handle on the Notes & Media composer so the
    // quote save can commit a note staged there (see handleSave). Null when the
    // strip is collapsed/unmounted, which makes the commit a safe no-op.
    const noteComposerRef = useRef(null);
    const [preallocQuoteId, setPreallocQuoteId] = useState(null);
    useEffect(() => {
        if (editingQuote?.id) { newQuoteRefRef.current = null; setPreallocQuoteId(null); return; }
        if (!orgId) return; // no org yet → strip stays hidden, save no-ops (corr. 1a)
        if (newQuoteRefRef.current) return; // already minted this session — never replace
        const ref = doc(collection(db, quotesPath)); // single hoisted path source
        newQuoteRefRef.current = ref;
        setPreallocQuoteId(ref.id);
    }, [editingQuote?.id, orgId]);
    // Id the Notes strip attaches to: existing id when editing, else pre-minted.
    const notesQuoteId = editingQuote?.id || preallocQuoteId;
    // r1-2c-qfm-notesstrip-a4c8f2d5 — collapsed strip state + live note list
    // (matches QuoteDetailsView). useOrgNotes bails to [] when the key is null.
    const [notesExpanded, setNotesExpanded] = useState(false);
    const [composerDirty, setComposerDirty] = useState(false);
    const { notes: quoteNotes } = useOrgNotes({
        orgId,
        attachedKey: notesQuoteId ? `quote:${notesQuoteId}` : null,
    });
    // r1-2c-qfm-exitguard-e6b1d093 — warn on browser refresh/close with staged
    // content (React cleanup is skipped there, so this is the only signal).
    useEffect(() => {
        if (!composerDirty) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [composerDirty]);
    // r1-2c-qfm-exitguard-e6b1d093 — guarded close for X / Cancel. Warn if there
    // is unsaved staged content OR posted notes stranded on the un-saved quote
    // id; on confirm DELETE the stranded POSTED notes (onNoteDeleted cascades
    // their photos/thumbnails/notesMedia). NEW-quote only — a saved quote's real
    // notes are never touched (corr. 3a/3d). Author can delete per rules:551.
    const handleGuardedClose = async () => {
        const strandable = !editingQuote?.id && !!preallocQuoteId;
        // quoteNotes is the live useOrgNotes subscription — it lists only POSTED
        // note docs. A photo still UPLOADING when Cancel is pressed has no note
        // doc yet, so it is absent from quoteNotes and is NOT deleted here — and
        // that is correct: NoteComposer's unmount cleanup (NoteComposer.js:132-154)
        // cancels the in-flight upload and removes the staged media. Likewise a
        // note typed/staged but never posted has no doc and is handled there too.
        // Do NOT add a staged-photo delete here — it would double-delete.
        const hasStranded = strandable && quoteNotes.length > 0;
        if ((composerDirty || hasStranded) &&
            !window.confirm('This quote has unsaved notes or photos that will be discarded. Leave without saving?')) return;
        if (hasStranded) {
            try {
                await Promise.all(quoteNotes.map(n => deleteDoc(doc(db, `organizations/${orgId}/notes`, n.id))));
            } catch (e) {
                console.warn('QuoteFormModal: failed to discard stranded quote notes', e);
            }
        }
        closeModal();
    };

    // ── Deposit config ────────────────────────────────────────────────────────
    // SEAM 6 VERIFIER: r1-dep-qfm-state-4a9c7e12
    // Held OUTSIDE quoteData deliberately. This modal spreads the whole existing
    // quote into quoteData and writes it back on save; depositStatus and every
    // deposit.* field except type/value are Cloud-Function-only money-truth and
    // firestore.rules rejects a client write that changes them. Keeping the
    // config in its own state lets handleSave write exactly {type, value} +
    // depositRequired and nothing else.
    const [depositEnabled,  setDepositEnabled]  = useState(false);
    const [depositType,     setDepositType]     = useState(DEPOSIT_TYPES.PERCENTAGE);
    const [depositValue,    setDepositValue]    = useState('');
    const [depositRequired, setDepositRequired] = useState(false);

    // ── Job Templates ──────────────────────────────────────────────────────────
    const [jobTemplates, setJobTemplates] = useState([]);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            snap => setJobTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [currentUserProfile?.organizationId]);

    // r1-seam0c-picker-3a6f8d24 — Seam 0c. The picker shows EVERY customer in the
    // org, because the rules now let any MANAGE_FINANCIALS holder write an estimate
    // for any customer in their own org (firestore.rules r1-seam0c-quotewrite-7e3b9a41).
    //
    // What used to be here was an ownership filter (`sales_person_id === my id`)
    // behind a hardcoded ['Admin','Manager','Owner/System Admin'] bypass. It hid
    // rows the rules would ALSO have rejected at save, so a Sales Consultant — and
    // Office Staff, who weren't on that bypass list either — got a blank, greyed-out
    // Customer field when opening a quote from another rep's lead, then a raw
    // "Missing or insufficient permissions." on Save.
    //
    // Ownership is now VISIBLE rather than enforced by hiding: the credit line
    // below the picker (r1-seam0c-creditline-5d1a8e60) names the rep this estimate
    // will be credited to. Credit itself is unchanged and is NOT decided here —
    // resolveCreateSalesPersonId stamps salesPersonId from the CUSTOMER's rep, so
    // quoting another rep's customer still credits that rep.
    //
    // No Field Technician exposure: this modal only mounts behind
    // can(profile, MANAGE_FINANCIALS) (quotelock-8b4e1c07 in CrmApp.js).
    const availableCustomers = useMemo(() => customers || [], [customers]);

    // --- 1. Fetch Tax Codes on Mount ---
    useEffect(() => {
        const fetchTaxCodes = async () => {
            if (!currentUserProfile?.organizationId) return;
            setIsLoadingTax(true);
            try {
                const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                const result = await getOptions();
                if (result.data?.taxCodes) {
                    setTaxOptions(result.data.taxCodes);
                }
            } catch (error) {
                console.error("Failed to load tax codes:", error);
            } finally {
                setIsLoadingTax(false);
            }
        };
        fetchTaxCodes();
    }, [currentUserProfile]);

    useEffect(() => {
        if (editingQuote) {
            const isNewQuoteForCustomer = !editingQuote.id && editingQuote.customer;
            
            let initialData = { ...editingQuote };

            if (isNewQuoteForCustomer) {
                const customer = editingQuote.customer;
                initialData.customerId = customer.id;
                initialData.customerName = customer.name;
                initialData.customerDetails = { 
                    name: customer.name,
                    email: customer.email || '',
                    phone: customer.phone || '',
                    address1: customer.address1 || '',
                    address2: customer.address2 || '',
                    city: customer.city || '',
                    state: customer.state || '',
                    zip: customer.zip || '',
                    country: customer.country || ''
                };
            }
            
            const ensuredLineItems = (initialData.lineItems && initialData.lineItems.length > 0)
                ? initialData.lineItems.map(item => ({
                    ...defaultLineItem, 
                    ...item,
                    type: item.type || 'Service',
                    taxCodeId: item.taxCodeId || '',
                    taxExempt: item.taxExempt || false,
                    // PHASE 3: backfill selective-approval fields if missing.
                    // Post-Phase-1 migration these should always be present,
                    // but be defensive in case an unmigrated record sneaks in.
                    itemId:        item.itemId        || generateItemId(),
                    selectionType: item.selectionType || DEFAULT_SELECTION_TYPE,
                    parentItemId:  Object.prototype.hasOwnProperty.call(item, 'parentItemId') ? item.parentItemId : null,
                }))
                : [makeNewLineItem()];

            setQuoteData(prev => ({
                ...prev,
                ...initialData,
                lineItems: ensuredLineItems,
                createdAt: (initialData.createdAt?.toDate?.() || new Date(initialData.createdAt || today)).toISOString().split('T')[0],
                // qvalid-qfm-legacyprefill-8e2a6c14 — Seam 2 bug fix: an EXISTING document
                // saved without an expiry (e.g. every phone-created estimate before Seam 2)
                // stays blank. It used to fall back to defaultExpiry, so a plain Save
                // silently gave an old estimate a brand-new expiry date.
                expiresAt: (editingQuote.id && !initialData.expiresAt)
                    ? ''
                    : (initialData.expiresAt?.toDate?.() || new Date(initialData.expiresAt || defaultExpiry)).toISOString().split('T')[0],
            }));

            // PROPERTIES v3 — sync the service-location dropdown to the loaded/pre-set quote.
            const _preHasProps = (editingQuote.customer?.properties || []).length > 0;
            setServiceLocation(editingQuote.propertyId ? editingQuote.propertyId : (_preHasProps ? '' : 'main'));
        }
    }, [editingQuote, today, defaultExpiry]);

    // SEAM 6 VERIFIER: r1-dep-qfm-init-8f2b4d63
    // Seed the deposit controls. A saved config always wins; a brand-new
    // estimate falls back to the org's depositRequiredDefault via
    // resolveDepositRequired (?? chain, so an explicit per-estimate false
    // still beats an org default of true). "Collect" starts on when the
    // estimate already carries a deposit, or when a new one lands in an org
    // that requires deposits by default. Kept as its own effect so it can
    // depend on companySettings without re-running the loader above.
    //
    // ORG SETTINGS SEAM: the org's default deposit AMOUNT (depositDefaults,
    // set in Settings → Deposits) seeds type/value too — but only on a
    // brand-new estimate. A saved config always wins, so re-opening an
    // existing estimate can never be rewritten by a later change to the org
    // default. Guarded on value > 0 so a half-configured default (enabled
    // with no amount) can't switch "Collect" on with an empty box.
    useEffect(() => {
        // SEAM 1d VERIFIER: qfm-seed-restore-7a1f4c9e
        // Seed a new estimate from the org's deposit default whenever the org
        // EXPLICITLY set one (defaults.enabled && value > 0). Since Seam 1c an
        // org without Stripe can require and collect a deposit offline, so
        // seeding no longer checks Stripe — it can only ever resume a default
        // the org itself turned on, never invent one. A SAVED config (the
        // hasSaved path) always loads as-is so existing deposits stay removable.
        const saved      = editingQuote?.deposit;
        const savedValue = Number(saved?.value);
        const hasSaved   = Number.isFinite(savedValue) && savedValue > 0;
        const required   = resolveDepositRequired(editingQuote?.depositRequired, companySettings?.depositRequiredDefault);

        const isNew    = !editingQuote?.id;
        const defaults = resolveDepositDefaults(companySettings?.depositDefaults);
        const useOrg   = isNew && defaults.enabled && defaults.value > 0;

        setDepositType(
            isValidDepositType(saved?.type) ? saved.type
                : useOrg ? defaults.type
                : DEPOSIT_TYPES.PERCENTAGE
        );
        setDepositValue(hasSaved ? String(savedValue) : useOrg ? String(defaults.value) : '');
        setDepositRequired(required);
        // depdraft-3c8e5a14 — a Draft saved with "Collect" ON and no amount yet stores depositAmountNeeded: true
        // (value 0, exactly what OFF stores), so the marker is what keeps the switch ON when it is reopened.
        setDepositEnabled(hasSaved || editingQuote?.depositAmountNeeded === true || (isNew && (required || useOrg)));
        // Deps are PRIMITIVES, not the depositDefaults object: onSnapshot hands
        // back a fresh object on every org-doc write, so depending on the object
        // would re-run this seed — and blow away whatever the operator had typed
        // into the deposit box — any time an unrelated org field changed.
    }, [
        editingQuote,
        companySettings?.depositRequiredDefault,
        companySettings?.depositDefaults?.enabled,
        companySettings?.depositDefaults?.type,
        companySettings?.depositDefaults?.value,
    ]);

    // SEAM 9 VERIFIER: r1-dep-qfm-approvalgate-2c7f9b18
    // Approval honesty. Mirror firestore.rules statusApproveSafe(): a saved
    // estimate whose deposit is configured + required + unsettled cannot be
    // moved to Approved by a client write (the rules reject it). Grey the
    // option out and explain, instead of surfacing a raw permission error.
    // Judged on the persisted editingQuote — that's the pre-write image the
    // rule's "settled" check reads. "required" resolves via the same ?? chain
    // the server uses; "settled" counts BOTH 'paid' and 'recorded', matching
    // depositSettled() in the rules (not a plain !== 'paid').
    const approvalBlockedByDeposit = useMemo(() => {
        const dep = editingQuote?.deposit;
        const configured = !!dep?.type && Number(dep?.value) > 0;
        const required = resolveDepositRequired(
            editingQuote?.depositRequired,
            companySettings?.depositRequiredDefault
        );
        // 1c-qfm-settled-5b2e8f04 — Seam 1c: 'waived' is a settled state too
        // (operator chose to proceed without collecting). Kept in sync with
        // depositSettled() in firestore.rules, which now accepts 'waived'.
        // sdrefund3-qfm-8e1c5a3f -- a refunded deposit is a settled (resolved) state
        // too: it must not re-trigger the "deposit unsettled" edit block.
        const settled = ['paid', 'recorded', 'waived', 'refunded'].includes(editingQuote?.depositStatus);
        return configured && required && !settled;
    }, [editingQuote, companySettings?.depositRequiredDefault]);

    // seamF2-state-9a3c1f8d — while the optional-deposit approval confirm is
    // pending, holds { amount }. Set instead of saving; cleared on Cancel
    // (saves nothing) or carried into the confirmed re-save.
    const [depositApprovalConfirm, setDepositApprovalConfirm] = useState(null);

    // r1-p1-docterm-qfm-title-9a2c8d51
    // Create-only: keep the Quote/Estimate Title field in sync with the
    // chosen doc-type word — org default until the picker is touched, then
    // whatever the picker shows — UNLESS the operator has typed a custom
    // title or a template set one. A title still equal to a bare doc-type
    // word ("Quote"/"Estimate") counts as auto and is safe to replace.
    // Guarded by !editingQuote?.id, so a saved title is never disturbed.
    useEffect(() => {
        if (editingQuote?.id) return;
        const qWord = resolveDocTerminology(DOC_TYPES.QUOTE).word;
        const eWord = resolveDocTerminology(DOC_TYPES.ESTIMATE).word;
        const word  = resolveDocTerminology(quoteData.docType ?? companySettings?.quoteTerminologyDefault).word;
        setQuoteData(prev => {
            const isAuto = !prev.title || prev.title === qWord || prev.title === eWord;
            return (isAuto && prev.title !== word) ? { ...prev, title: word } : prev;
        });
    }, [editingQuote?.id, quoteData.docType, companySettings?.quoteTerminologyDefault]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setQuoteData(prev => ({ ...prev, [name]: value }));
    };


    const handleCustomerChange = (e) => {
        const customerId = e.target.value;
        const customer = customers.find(c => c.id === customerId);
        const hasProperties = (customer?.properties || []).length > 0;
        setQuoteData(prev => ({
            ...prev,
            customerId: customerId,
            customerName: customer ? customer.name : '',
            customerDetails: customer ? {
                name: customer.name,
                email: customer.email || '',
                phone: customer.phone || '',
                address1: customer.address1 || '',
                address2: customer.address2 || '',
                city: customer.city || '',
                state: customer.state || '',
                zip: customer.zip || '',
                country: customer.country || ''
            } : {},
            // PROPERTIES v3 — reset service location whenever the customer changes.
            propertyId: null, propertyName: null, serviceAddress: null,
            billTo: null, // billto-qfm-custreset-6b3a8d19 -- bill-to never survives a customer switch
        }));
        setServiceLocation(hasProperties ? '' : 'main');
        setPendingThirdParty(false);
        setBillToTouched(false);
    };

    // PROPERTIES v3 — user picked a service location: '' unchosen | 'main' | property id.
    // Bill-to (customerDetails) is unchanged; this only sets the service-location fields.
    const handleServiceLocationChange = (e) => {
        const value = e.target.value;
        setServiceLocation(value);
        const customer = customers.find(c => c.id === quoteData.customerId);
        const prop = (value && value !== 'main')
            ? (customer?.properties || []).find(p => p.id === value)
            : null;
        // billto-qfm-handlers-2f7d4b81 -- apply the property's default bill-to when the user
        // has not touched the Bill To selector; a propertyContact bill-to never outlives
        // its property. Blocks are built ONLY by the shared builder (src/utils/billTo.js).
        setQuoteData(prev => {
            let billTo = prev.billTo;
            if (!billToTouched) {
                const def = prop?.defaultBillTo;
                if (def?.mode === 'propertyContact') billTo = buildBillToFromProperty(prop, customer);
                else if (def?.mode === 'thirdParty') billTo = buildBillToFromCustomer(customers.find(c => c.id === def.thirdPartyCustomerId));
                else billTo = null;
            } else if (billTo?.mode === 'propertyContact' && (!prop || prop.id !== billTo.propertyId)) {
                billTo = null;
            }
            return {
                ...prev,
                propertyId: prop ? prop.id : null,
                propertyName: prop ? (prop.name || '') : null,
                serviceAddress: prop ? (prop.addressCanonical || buildAddressString(prop)) : null,
                billTo,
            };
        });
        if (!billToTouched) setPendingThirdParty(false);
    };

    const handleBillToChoiceChange = (e) => {
        const choice = e.target.value;
        setBillToTouched(true);
        if (choice === 'thirdParty') {
            setPendingThirdParty(true);
            setQuoteData(prev => ({ ...prev, billTo: prev.billTo?.mode === 'thirdParty' ? prev.billTo : null }));
            return;
        }
        setPendingThirdParty(false);
        if (choice === 'propertyContact') {
            const cust = customers.find(c => c.id === quoteData.customerId);
            const p = (cust?.properties || []).find(x => x.id === quoteData.propertyId);
            setQuoteData(prev => ({ ...prev, billTo: buildBillToFromProperty(p, cust) }));
        } else {
            setQuoteData(prev => ({ ...prev, billTo: null }));
        }
    };

    const handleBillToThirdPartyChange = (e) => {
        const third = customers.find(c => c.id === e.target.value);
        setBillToTouched(true);
        setPendingThirdParty(!third);
        setQuoteData(prev => ({ ...prev, billTo: buildBillToFromCustomer(third) }));
    };

    const handleLineItemChange = (index, e) => {
        const target = e.target; 
        const name = target.name; 
        
        const items = [...quoteData.lineItems];
        const currentItem = { ...items[index] };
        
        if (target.type === 'checkbox') {
            currentItem[name] = target.checked;
            if (name === 'taxExempt' && !target.checked) {
                currentItem.taxExemptionNumber = '';
            }
        } else {
            currentItem[name] = (name === 'quantity' || name === 'price') 
                ? (target.value === '' ? '' : parseFloat(target.value)) 
                : target.value;
        }

        // PHASE 3 VERIFIER: r1-p3-qfm-selxnchange-9b2e3f47
        // Side effects when selectionType changes:
        //   - When switching TO 'included': default price to 0 (the
        //     package-template pattern) and clear parentItemId so the
        //     operator is forced to consciously pick a parent. The
        //     parent-picker UI surfaces immediately below.
        //   - When switching AWAY from 'included': clear parentItemId.
        //     Price is left as-is — per Marcus's design call, don't
        //     surprise the operator with automatic reverts.
        if (name === 'selectionType') {
            if (target.value === SELECTION_TYPES.INCLUDED) {
                currentItem.price = 0;
                currentItem.parentItemId = null;
            } else {
                currentItem.parentItemId = null;
            }
        }
        
        items[index] = currentItem;
        setQuoteData(prev => ({ ...prev, lineItems: items }));
    };

    const handleServiceSelect = (index, serviceId) => {
        const service = services.find(s => s.id === serviceId);
        const items = [...quoteData.lineItems];
        if (service) {
            items[index] = {
                ...items[index], 
                serviceId: serviceId,
                name: service.name,
                description: service.description,
                price: service.price,
                type: service.type || 'Service', 
                taxCodeId: service.taxCodeId || '', 
                addToCatalog: false
            };
        } else {
             items[index] = { 
                 ...items[index], 
                 serviceId: '', 
                 addToCatalog: false,
                 taxCodeId: '' 
            };
        }
        setQuoteData(prev => ({ ...prev, lineItems: items }));
    };

    const addLineItem = () => {
        setQuoteData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, makeNewLineItem()] 
        }));
    };

    const removeLineItem = (index) => {
        const items = quoteData.lineItems.filter((_, i) => i !== index);
        setQuoteData(prev => ({ ...prev, lineItems: items }));
    };

    // --- UPDATED CALCULATION LOGIC ---
    const calculations = useMemo(() => {
        let subtotal = 0;
        let totalTaxAmount = 0;

        quoteData.lineItems.forEach(item => {
            const qty = Number(item.quantity) || 0;
            const price = Number(item.price) || 0;
            const lineTotal = qty * price;
            
            subtotal += lineTotal;

            if (!item.taxExempt) {
                // 1. Try to find specific QB rate
                let specificTax = taxOptions.find(t => t.Id === item.taxCodeId);
                
                // 2. If "Default Tax" is selected, look up the Company QB Default
                if (!specificTax && !item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    specificTax = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                }

                // 3. Determine the rate to use
                let applicableRate = 0;
                if (specificTax) {
                    applicableRate = specificTax.Rate; // Use QB Rate
                } else {
                    // 4. Fallback to Manual Setting
                    applicableRate = companySettings?.taxRate || 0; 
                }

                totalTaxAmount += lineTotal * (applicableRate / 100);
            }
        });

        return {
            subtotal,
            taxAmount: totalTaxAmount,
            total: subtotal + totalTaxAmount
        };
    }, [quoteData.lineItems, companySettings, taxOptions]);

    // SEAM 6 VERIFIER: r1-dep-qfm-locked-3d7a5f28
    // depositStatus only exists once approveQuoteV2 has resolved the deposit,
    // and deposit.amountResolved is the number Stripe Checkout charges — a
    // config edit after that would silently desync from it. firestore.rules
    // enforces the same freeze; this just stops the UI offering the edit.
    const depositLocked = !!quoteData.depositStatus;

    // SEAM F2 VERIFIER: seamF2-detect-c7f2a3e1
    // Optional-deposit approval signal. The REQUIRED case (approvalBlockedByDeposit
    // above) is greyed out and rules-blocked. The OPTIONAL case is NOT blocked —
    // moving such an estimate to Approved by direct write (this modal's setDoc)
    // puts the deposit OWING via the onQuoteStatusChanged heal (Seam F1), rather
    // than silently discarding it. This memo detects that case and computes the
    // amount the heal WILL stamp, so the operator is told the same number.
    //
    // Mirrors the F1 heal (functions/index.js SEAM F1 HEAL a3e8b6d2) exactly:
    //   - only when no depositStatus yet exists (depositLocked false → the deposit
    //     config is actually written and the heal can fire; a locked deposit is
    //     already in play and the heal no-ops),
    //   - only for the values THIS save writes (depositType/value/required come
    //     from the modal's own deposit controls, which is exactly what depositFields
    //     writes while depositLocked is false),
    //   - only when the estimate is NOT already approved/accepted (matches the
    //     heal's transition guard; a re-save of an Approved estimate never fires),
    //   - amount base = finite positive approvedTotal else the live total, run
    //     through the same resolveDepositAmount helper the server uses.
    // Returns null (no signal) whenever the heal would no-op — including a deposit
    // that resolves to 0 — so the confirm and the money stay in lockstep.
    const optionalDepositOnApproval = useMemo(() => {
        if (depositLocked) return null;                       // already in play → heal no-ops
        if (!depositEnabled || depositRequired) return null;  // required is the greyed case
        const alreadyApproved = ['approved', 'accepted'].includes((editingQuote?.status || '').toLowerCase());
        if (alreadyApproved) return null;
        const value = Number(depositValue);
        if (!Number.isFinite(value) || value <= 0) return null;
        const approvedTotalNum = Number(quoteData.approvedTotal);
        const base = (Number.isFinite(approvedTotalNum) && approvedTotalNum > 0)
            ? approvedTotalNum
            : calculations.total;
        const amount = resolveDepositAmount(depositType, value, base);
        if (!(amount > 0)) return null;                       // matches the heal's amount > 0 gate
        return { amount };
    }, [depositLocked, depositEnabled, depositRequired, depositValue, depositType, editingQuote?.status, quoteData.approvedTotal, calculations.total]);

    // SEAM 1d VERIFIER: qfm-stripeready-note-4b2e8d1a
    // isOrgStripeReady now drives ONLY the neutral offline-collection note
    // below — it no longer disables any authoring control. An org without
    // Stripe states the deposit on the quote and collects it offline (cheque,
    // cash, e-transfer, or its own terminal), then records it.
    const stripeReady = canOrgTakeOnlinePayments(companySettings); // payhide-7e4c1b05

    // Preview only. The server re-resolves against the APPROVED total at
    // approval time (declined optional lines shrink it), so this is an
    // estimate on the current total, not a promise.
    const depositPreview = useMemo(
        () => resolveDepositAmount(depositType, depositValue, calculations.total),
        [depositType, depositValue, calculations.total]
    );

    // ── Apply a template to the quote ─────────────────────────────────────────
    // PHASE 3 VERIFIER: r1-p3-qfm-applytemplate-6a8f4d27
    // Templates carry selectionType / parentItemId from the operator's
    // template definition. We MUST mint a fresh itemId per quote line
    // (templates store their own itemIds — using them on every quote
    // would conflict if the template is applied to multiple quotes).
    //
    // parentItemId remapping: the template's parentItemId points to
    // OTHER template line items by their template-itemId. We need to
    // remap those references to the new quote-line itemIds.
    const applyTemplate = (template) => {
        if (!template) return;

        // lineidweb-9e4a2d17 — the one shared template function (target 'quote'): fresh itemIds with
        // parentItemId remapped to them, selectionType defaulted, Tax Exempt (tmplexempt-5c9d1f38) and
        // the tax code carried — exactly what this block built before — plus a fresh lineId per line.
        const processedItems = lineIds.templateLinesFor(template.lineItems, { target: 'quote', newId: lineIds.newLineId, newItemId: generateItemId });

        setQuoteData(prev => ({
            ...prev,
            title: template.name || prev.title,
            notes: [template.defaultNotes, prev.notes].filter(Boolean).join('\n'),
            lineItems: processedItems.length > 0 ? processedItems : prev.lineItems,
            // PHASE FU-QC VERIFIER: r1-fu-qc-5b2d8c41
            estimatedDuration: template.estimatedDuration || 0,
        }));
        setShowTemplatePicker(false);
    };

    const handleSave = async (sendEmail = false, skipDepositConfirm = false) => { // seamF2-sig-2a7f4c93
        if (!quoteData.customerId) {
            setError("Please select a customer.");
            return;
        }
        // PROPERTIES v3 — force a service-location choice when the customer has properties.
        const _selCustForLoc = customers.find(c => c.id === quoteData.customerId);
        if ((_selCustForLoc?.properties || []).length > 0 && serviceLocation === '') {
            setError("This customer has multiple service locations. Please choose which one.");
            return;
        }
        // Validate line items have names
        const unnamedIndex = (quoteData.lineItems || []).findIndex(item => !(item.name || '').trim());
        if (unnamedIndex !== -1) {
            setError(`Line item ${unnamedIndex + 1} needs a name before saving.`);
            return;
        }

        // PHASE 3 VERIFIER: r1-p3-qfm-validation-3e7d2a59
        // Selective-approval rules:
        //   1. Every Included item must have parentItemId set
        //   2. parentItemId must point to a real line in this quote
        //   3. The referenced parent cannot itself be Included
        //      (single-level nesting only — keeps mental model sane)
        const items = quoteData.lineItems || [];
        const itemById = new Map(items.filter(i => i.itemId).map(i => [i.itemId, i]));
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.selectionType !== SELECTION_TYPES.INCLUDED) continue;

            if (!item.parentItemId) {
                setError(`Line ${i + 1} ("${item.name || 'unnamed'}") is marked Included but has no parent line selected. Please choose a parent or change its type.`);
                return;
            }
            const parent = itemById.get(item.parentItemId);
            if (!parent) {
                setError(`Line ${i + 1} ("${item.name || 'unnamed'}") references a parent that no longer exists. Please pick a different parent or change its type.`);
                return;
            }
            if (parent.selectionType === SELECTION_TYPES.INCLUDED) {
                setError(`Line ${i + 1} ("${item.name || 'unnamed'}") is Included under another Included item. Pick a Required, Recommended, or Add-on line as the parent instead.`);
                return;
            }
        }

        // SEAM 6 VERIFIER: r1-dep-qfm-validate-2e8a5c94
        // A deposit with no positive value resolves to 0 on the server, which
        // means no deposit and no approval hold — the operator would think they
        // configured one. Block it here rather than let it fail silently.
        // depdraft-3c8e5a14 — EXCEPT a plain Save of a Draft: it may keep "Collect" ON with no amount yet
        // (depositFields stores depositAmountNeeded: true). Sending — Save & Send, or the status set to Sent —
        // says so in the send words; every other status keeps today's message.
        if (!depositLocked && depositEnabled) {
            const v = Number(depositValue);
            if (!Number.isFinite(v) || v <= 0) {
                const statusNow = String(quoteData.status || '').toLowerCase();
                if (sendEmail || statusNow === 'sent') {
                    setError('Enter the deposit amount before sending.');
                    return;
                }
                if (statusNow !== 'draft') {
                    setError('Enter a deposit amount greater than zero, or turn off "Collect a deposit".');
                    return;
                }
            } else if (depositType === DEPOSIT_TYPES.PERCENTAGE && v > 100) {
                setError("A percentage deposit can't be more than 100%.");
                return;
            }
        }

        // billto-qfm-guard-7a5e2d94 -- "Another customer..." chosen but nobody picked yet.
        if (pendingThirdParty && !quoteData.billTo) {
            setError('Choose which customer to bill, or set Bill to back to Main customer.');
            return;
        }

        // seamF2-gate-6e2b8d4f — active signal before an optional-deposit approval.
        // If this save moves the estimate INTO Approved (a plain Save, not a Send —
        // Send forces status 'Sent', so the heal never fires) and it carries a
        // configured, OPTIONAL, not-yet-in-play deposit, stop and confirm first.
        // Cancel clears the pending state and returns here without touching
        // isSaving, so nothing whatsoever is written; the confirm re-enters this
        // handler with skipDepositConfirm=true and everything proceeds as normal.
        // optionalDepositOnApproval is null for the required case (greyed option),
        // no-deposit estimates, and already-Approved estimates — those never confirm.
        if (!skipDepositConfirm && !sendEmail && quoteData.status === 'Approved' && optionalDepositOnApproval) {
            setDepositApprovalConfirm({ amount: optionalDepositOnApproval.amount });
            return;
        }

        setError('');
        setIsSaving(true);

        // r1-2c-qfm-path-1a2b3c4d — orgId + quotesPath now hoisted to component
        // scope (see above) so the create write and the pre-alloc effect agree.
        const servicesPath = `organizations/${orgId}/services`;

        const processedLineItems = await Promise.all(quoteData.lineItems.map(async (item) => {
            if (item.addToCatalog && !item.serviceId && item.name) {
                try {
                    const newServiceRef = await addDoc(collection(db, servicesPath), {
                        name: item.name,
                        description: item.description || '',
                        price: Number(item.price),
                        type: item.type || 'Service',
                        unit: 'Unit', 
                        taxCodeId: item.taxCodeId || '', 
                        createdAt: serverTimestamp()
                    });
                    
                    return {
                        ...item,
                        serviceId: newServiceRef.id,
                        addToCatalog: false 
                    };
                } catch (e) {
                    console.error("Failed to save new catalog item:", e);
                    return item; 
                }
            }
            const { addToCatalog, ...cleanItem } = item;
            return cleanItem;
        }));

        const isBeingSent     = sendEmail || quoteData.status === 'Sent';
        const isBeingClosed  = ['Approved', 'Declined'].includes(quoteData.status);
        // qvalid-qfm-manualsent-1b7d5e29 — Seam 2: marking an estimate Sent by hand
        // (status dropdown + plain Save) starts the follow-up reminder clock exactly
        // like Save & Send. TRANSITION only — the status was not already Sent when the
        // editor opened — so re-saving a Sent estimate never resets the clock. A new
        // document has no prior status, so creating one straight as Sent counts.
        // Safe because follow-up reminders go to the OPERATOR, never the customer; if
        // a client-facing reminder ladder is ever built, re-decide this trigger.
        const isTransitioningToSent = quoteData.status === 'Sent' && (editingQuote?.status || '').toLowerCase() !== 'sent';

        // qexp-6f2a9d38-qfm — Seam 5: never SEND an already-expired estimate.
        //
        // WHY THIS MATTERS NOW. 19 estimates in production sit in Draft with an
        // expiry date already in the past (Seam 2 deliberately stopped refreshing
        // expiry on save, so a draft ages in place). Before Seam 5 that only meant
        // the email said "valid until <a past date>" — embarrassing. After Seam 5
        // the portal BLOCKS approval on an expired estimate, so sending one would
        // put the customer in front of an estimate they cannot accept.
        //
        // NOT A CONTRADICTION OF qvalid-qfm-blankexpiry-6d3c8f51. That fix stopped
        // a plain SAVE from silently re-dating an estimate. This fires only on the
        // TRANSITION INTO SENT — the moment the document actually reaches the
        // customer, and the same trigger Seam 2 already uses to start the
        // follow-up clock. Re-saving an already-Sent estimate re-dates nothing, so
        // a genuinely expired estimate still needs a deliberate re-issue.
        // Resolved into a LOCAL, never written back to state — dataToSave below
        // reads this instead of quoteData.expiresAt.
        const sendingNow = sendEmail || isTransitioningToSent;
        let expiresAtForSave = quoteData.expiresAt;
        if (sendingNow) {
            // Compare on the LOCAL calendar day, matching resolveExpiryState's
            // "valid through the expiry date" rule: an estimate expiring today is
            // still fine to send.
            const existingDay = expiresAtForSave ? formatLocalDate(parseLocalDate(expiresAtForSave)) : null;
            if (!existingDay || existingDay < today) {
                expiresAtForSave = defaultExpiry;
            }
        }

        // SEAM 6 VERIFIER: r1-dep-qfm-save-6b3f9d27
        // Strip every deposit field that rode in on the `...editingQuote` spread,
        // then re-add only the two the client owns. depositStatus and
        // deposit.amountResolved/currency/remindersSent/paidAt are
        // Cloud-Function-only; firestore.rules rejects a client write that
        // changes them, so they must never leave this modal.
        // qpipe-4d1a7c35-qfm-strip — Seam 4: pipeline stage fields are
        // Cloud-Function-only too (firestore.rules pipelineWriteSafe). Stripping
        // them means a stale open modal (the server moved the stage meanwhile)
        // can't write an old stage back and hit permission-denied; the merge
        // leaves the stored values untouched. outcomeType is never stored but is
        // stripped in case an old in-memory copy carries it.
        // h1quote-5c2e8a19 — H1 Seam 5: the customer's approval and the job conversion are
        // server-only too (firestore.rules approvalWriteSafe), so they never leave this modal
        // either: a stale copy (e.g. a conversion the server has since cleared) would otherwise be
        // written back and refused. `customer` is the full customer record three entry points open
        // this modal with (new estimate from a lead, from the customer's Estimates tab, Re-quote
        // Declined); it is only for pre-filling and must never be saved onto the estimate, where the
        // customer portal can read it.
        const {
            deposit: _deposit, depositStatus: _depositStatus, depositRequired: _depositRequired,
            pipelineStageId: _pipelineStageId, pipelineStageSetAt: _pipelineStageSetAt, outcomeType: _outcomeType,
            approvedAt: _approvedAt, selectionsLocked: _selectionsLocked, approvedItemIds: _approvedItemIds,
            declinedItemIds: _declinedItemIds, approvedSubtotal: _approvedSubtotal, approvedTaxAmount: _approvedTaxAmount,
            approvedTotal: _approvedTotal, latestApprovalId: _latestApprovalId,
            convertedToJobId: _convertedToJobId, convertedAt: _convertedAt,
            customer: _customer,
            approvedTaxParts: _approvedTaxParts, approvedTotalsEngineVersion: _approvedTotalsEngineVersion, // taxlines2-3e0b16a4 — the approval's tax parts: server-owned, never echoed
            taxExemptionOff: _taxExemptionOff, taxExemptionHistory: _taxExemptionHistory, // taxexempt3-8c4f2a1d — server-owned (setDocumentTaxExemption), never echoed
            ...quoteFields
        } = quoteData;

        // Pre-approval only. Once depositStatus exists the config is frozen, so
        // we omit the fields entirely and the merge leaves the stored values
        // untouched. Disabling writes value:0 rather than deleting the map:
        // Firestore's merge can't remove map sub-fields, the rules only permit
        // {type, value}, and the server already treats a 0 amount as no deposit.
        // depdraft-3c8e5a14 — "Collect" ON with no amount (a Draft): value 0 + depositAmountNeeded: true. The
        // marker is written on EVERY unlocked save (true or false), so it never outlives the state it describes.
        const depositAmountMissing = !(Number(depositValue) > 0);
        const depositFields = depositLocked ? {} : {
            deposit: {
                type:  depositType,
                value: depositEnabled && !depositAmountMissing ? Number(depositValue) : 0,
            },
            depositRequired: depositEnabled ? !!depositRequired : false,
            depositAmountNeeded: depositEnabled && depositAmountMissing,
        };

        const dataToSave = {
            ...quoteFields,
            ...calculations,
            lineItems: lineIds.ensureLineIds(processedLineItems, lineIds.newLineId), // lineidweb-9e4a2d17 — every line saved with a unique lineId (older estimates get theirs here)
            createdAt: parseLocalDate(quoteData.createdAt),
            // qexp-6f2a9d38-qfm — expiresAtForSave, not quoteData.expiresAt: on a
            // send it carries the refreshed date for an estimate whose own was
            // already past. Identical on every other save.
            expiresAt: parseLocalDate(expiresAtForSave),
            status: sendEmail ? 'Sent' : quoteData.status,
            updatedAt: serverTimestamp(),
            // lastact-w1 — the operator saved this estimate. One stamp covers BOTH
            // branches below: the create path spreads ...dataToSave, so there is
            // deliberately no second write site to keep in sync. updatedAt above is
            // untouched; this seam is additive.
            lastActivityAt: serverTimestamp(),
            ...depositFields,
            // Quote follow-up reminder tracking
            ...((sendEmail || isTransitioningToSent) ? {
                sentAt: serverTimestamp(),
                followUpRemindersEnabled: true,
                followUpRemindersSent: [],
            } : {}),
            ...(isBeingClosed ? {
                followUpRemindersEnabled: false,
            } : {}),
        };
        // qvalid-qfm-blankexpiry-6d3c8f51 — Seam 2 bug fix: a blank expiry writes
        // nothing. parseLocalDate('') returns NOW, so a blank field used to stamp an
        // already-expired date. Deleting the key also drops the '' that rode in on
        // ...quoteFields, and the merge leaves any stored value untouched.
        // qexp-6f2a9d38-qfm — reads expiresAtForSave so a SEND still stamps a date
        // on an estimate that had none. A blank expiry on a plain Save behaves
        // exactly as before.
        if (!expiresAtForSave) delete dataToSave.expiresAt;
        // taxlines2-3e0b16a4 — this editor re-totals tax its own way, so an edit drops the stored tax parts
        // (they'd no longer add up). Never on a create (no merge there; a new estimate has none).
        dropStaleTaxParts(dataToSave, { isEdit: !!editingQuote?.id, retotals: true, deleteValue: deleteField() });

        try {
            let quoteId = editingQuote?.id;

            if (editingQuote?.id) {
                await setDoc(doc(db, quotesPath, editingQuote.id), dataToSave, { merge: true });
            } else {
                // r1-p1-docterm-qfm-stamp-8c3e5b74 — number prefix + docType from one resolve, so they can't disagree
                const term = resolveDocTerminology(quoteData.docType ?? companySettings?.quoteTerminologyDefault);
                // r1-2c-qfm-savepath-3b9e6c12 — write to the id minted on open so
                // photos staged in the Notes strip already point at this quote.
                // setDoc (no merge) on a fresh id == a plain create, identical to
                // addDoc. docType is stamped here; quoteNumber is now assigned by
                // the setQuoteNumberOnCreate trigger (Seam 2A), not stamped here.
                const newQuoteRef = newQuoteRefRef.current ?? doc(collection(db, quotesPath));
                // Seam 0 — provenance/ownership stamped on CREATE only. createdBy =
                // the creating user; salesPersonId = the customer's rep, else the
                // creating user (resolveCreateSalesPersonId). requestId only rides
                // along when this quote was opened from a converted lead (change B).
                const selectedCustomer = customers.find(c => c.id === quoteData.customerId);
                await setDoc(newQuoteRef, {
                    ...dataToSave,
                    // qnumstrip-3e8c1a47 — Seam 2A: quoteNumber no longer stamped here; setQuoteNumberOnCreate assigns E-/Q-NNNN. docType stays (the trigger reads it for the prefix).
                    docType: term.type,
                    createdBy: currentUserProfile.id,
                    salesPersonId: resolveCreateSalesPersonId(selectedCustomer, currentUserProfile.id),
                    ...(editingQuote?.requestId ? { requestId: editingQuote.requestId } : {}),
                });
                quoteId = newQuoteRef.id;
            }

            // r1-2d-qfm-notecommit-7b3e9d02 — commit any note staged in the
            // Notes & Media strip as part of this save. Placed AFTER the quote
            // doc exists (so the note attaches to a real quote) and BEFORE the
            // send (sendQuoteEmail reads the quote from Firestore by id, so a
            // customer-facing note is visible to it). Best-effort: commit() is a
            // no-op when the strip is empty/unmounted and never throws, so a
            // failed note can never fail the quote save.
            try {
                await noteComposerRef.current?.commit();
            } catch (noteErr) {
                console.warn('QuoteFormModal: staged note commit failed', noteErr);
            }

            if (sendEmail) {
                try {
                    const sendQuote = httpsCallable(functions, 'sendQuoteEmail');
                    await sendQuote({ quoteId });
                } catch (emailErr) {
                    console.error("Quote saved but email failed:", emailErr);
                    setError("Quote saved successfully, but the email failed to send. You can resend it from the quote detail view.");
                    setIsSaving(false);
                    closeModal(stripFirestoreSentinels({ id: quoteId, ...dataToSave })); // sentstrip-9c3f6a28
                    return;
                }
            }

            setIsSaving(false);
            closeModal(stripFirestoreSentinels({ id: quoteId, ...dataToSave })); // sentstrip-7b1d4e85
        } catch (err) {
            console.error("Error saving quote:", err);
            // SEAM 9 — turn the rules' raw "missing or insufficient permissions"
            // into the actual reason, but ONLY for the deposit-approval guard.
            // Any other permission error keeps its original message.
            if (err?.code === 'permission-denied' && approvalBlockedByDeposit) {
                setError(`The deposit must be paid before this ${docWordLower} can be approved.`);
            } else {
                setError(err.message || "Failed to save quote. Please try again.");
            }
            setIsSaving(false);
        }
    };
    
    const currencyCode = companySettings?.currency || 'USD';
    // r1-dep-qfm-term-7a3c9e15 — same per-document resolve the title/header use:
    // the picker's value while creating, the stamped docType once saved.
    const docWordLower = resolveDocTerminology(quoteData.docType ?? companySettings?.quoteTerminologyDefault).word.toLowerCase();

    // r1-seam0c-creditline-5d1a8e60 — Seam 0c. Now that the picker shows every
    // customer in the org, a user can quote a customer who belongs to someone
    // else — and credit follows the CUSTOMER's rep, not the writer. Say so, so
    // nobody discovers it later in a sales report.
    //
    // Resolved through resolveCreateSalesPersonId — the SAME Seam 0 call the save
    // path uses to stamp salesPersonId — so the line can never claim a different
    // rep than the one actually written. That resolver is read here, never changed.
    //
    // Plain consts, not useMemo: two cheap .find()s, and no hook is added to a
    // component whose hook order is already long. Create-mode only — on an
    // existing estimate the credit was stamped at create and the Customer select
    // is disabled, so "will be credited" would be the wrong tense and the wrong
    // source. Hidden when the credited rep IS the current user, otherwise it
    // would show on every estimate a rep writes for their own customer.
    const creditCustomer = customers?.find(c => c.id === quoteData.customerId);
    const creditedRepId = resolveCreateSalesPersonId(creditCustomer, currentUserProfile?.id);
    const creditedRepName = (!editingQuote?.id && creditedRepId && creditedRepId !== currentUserProfile?.id)
        ? (orgUsers.find(u => u.id === creditedRepId)?.name
           || creditCustomer?.sales_person_name
           || 'another rep')
        : null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-6 pb-4">
                    {/* r1-p1-docterm-qfm-header-4e9a1c62 — per-doc word on edit; live-follows the picker on create */}
                    <h2 className="text-xl font-bold">{editingQuote?.id ? `Edit ${resolveDocTerminology(editingQuote.docType).word} #${editingQuote.quoteNumber}` : `Create New ${resolveDocTerminology(quoteData.docType ?? companySettings?.quoteTerminologyDefault).word}`}</h2>
                    <button onClick={handleGuardedClose} className="p-1 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>

                <div className="overflow-y-auto px-6 pb-2 flex-grow">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 border rounded-lg bg-gray-50">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Customer</label>
                            {/* custpick-8e5a2c71 — was a native <select> over the whole customer
                                list (availableCustomers here is unfiltered), so typing jumped
                                rather than narrowed. Same list passed in; handleCustomerChange is
                                used verbatim — the combobox emits the { target: { name, value } }
                                shape a select does, so the customerDetails snapshot, the
                                service-location reset and the bill-to wipe all still run. */}
                            <CustomerCombobox
                                name="customerId"
                                customers={availableCustomers}
                                value={quoteData.customerId}
                                onChange={handleCustomerChange}
                                disabled={!!editingQuote?.customer?.id}
                            />
                            {/* r1-seam0c-creditline-5d1a8e60 */}
                            {creditedRepName && (
                                <p className="mt-1 text-xs text-gray-600">
                                    This {docWordLower} will be credited to <span className="font-semibold">{creditedRepName}</span>.
                                </p>
                            )}
                        </div>
                        {/* PROPERTIES v3 — Service location selector (only when the customer has properties) */}
                        {(() => {
                            const selCust = customers.find(c => c.id === quoteData.customerId);
                            const props = selCust?.properties || [];
                            if (props.length === 0) return null;
                            return (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Service location <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={serviceLocation}
                                        onChange={handleServiceLocationChange}
                                        className={`mt-1 w-full p-2 border rounded-md bg-white ${serviceLocation === '' ? 'border-red-400' : ''}`}
                                    >
                                        <option value="">-- Choose a service location --</option>
                                        <option value="main">Main address{selCust ? ` — ${buildAddressString(selCust)}` : ''}</option>
                                        {props.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {(p.name || p.address1 || 'Unnamed property')} — {p.addressCanonical || buildAddressString(p)}
                                            </option>
                                        ))}
                                    </select>
                                    {serviceLocation === '' && (
                                        <p className="mt-1 text-xs text-red-500">This customer has multiple locations — choose which one.</p>
                                    )}
                                </div>
                            );
                        })()}
                        {/* billto-qfm-ui-9e1c5a37 -- Bill To selector. Phase 1 stores the choice only;
                            PDFs, emails, QuickBooks and the portal still use the main customer. */}
                        {quoteData.customerId && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Bill to</label>
                                <select
                                    value={pendingThirdParty ? 'thirdParty' : (quoteData.billTo?.mode || 'main')}
                                    onChange={handleBillToChoiceChange}
                                    className="mt-1 w-full p-2 border rounded-md bg-white"
                                >
                                    <option value="main">Main customer</option>
                                    {quoteData.propertyId ? <option value="propertyContact">Property contact</option> : null}
                                    <option value="thirdParty">Another customer...</option>
                                </select>
                                {/* custpick-8e5a2c71 — the second full-list select in this modal,
                                    same treatment. handleBillToThirdPartyChange reads only
                                    e.target.value, so it is used verbatim. */}
                                {(pendingThirdParty || quoteData.billTo?.mode === 'thirdParty') && (
                                    <CustomerCombobox
                                        customers={customers.filter(c => c.id !== quoteData.customerId)}
                                        value={quoteData.billTo?.customerId || ''}
                                        onChange={handleBillToThirdPartyChange}
                                        emptyLabel="-- Choose the customer to bill --"
                                        invalid={!quoteData.billTo}
                                    />
                                )}
                            </div>
                        )}
                        {/* r1-p1-docterm-qfm-picker-6f4b2e93 — create-only doc-type picker + dynamic title label */}
                        {!editingQuote?.id && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Document type</label>
                                <select
                                    name="docType"
                                    value={quoteData.docType ?? (companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE)}
                                    onChange={handleChange}
                                    className="mt-1 w-full p-2 border rounded-md bg-white"
                                >
                                    <option value={DOC_TYPES.QUOTE}>Quote</option>
                                    <option value={DOC_TYPES.ESTIMATE}>Estimate</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{resolveDocTerminology(quoteData.docType ?? companySettings?.quoteTerminologyDefault).word} Title</label>
                            <input 
                                name="title" 
                                value={quoteData.title} 
                                onChange={handleChange} 
                                className="mt-1 w-full p-2 border rounded-md focus:ring-1 focus:ring-blue-500 outline-none" 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <select
                                name="status"
                                value={quoteData.status}
                                onChange={handleChange}
                                className="mt-1 w-full p-2 border rounded-md bg-white"
                            >
                                {quoteStatuses.map(status => (
                                    <option
                                        key={status}
                                        value={status}
                                        disabled={status === 'Approved' && approvalBlockedByDeposit}
                                    >
                                        {status}
                                    </option>
                                ))}
                            </select>
                            {approvalBlockedByDeposit && (
                                <p className="mt-1 text-xs text-amber-700">Deposit must be paid before approval.</p>
                            )}
                            {/* seamF2-note-3f9c7a21 — quieter inline signal for the OPTIONAL case:
                                approving marks the deposit owing rather than dropping it. Shown only
                                when Approved is selected for a not-yet-approved estimate carrying a
                                configured optional deposit; the confirm-on-save is the required signal. */}
                            {quoteData.status === 'Approved' && optionalDepositOnApproval && (
                                <p className="mt-1 text-xs text-gray-500">
                                    Approving marks this {docWordLower}'s {formatCurrency(optionalDepositOnApproval.amount, currencyCode)} deposit as owing. You can record a payment or waive it afterward.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Quote Date</label>
                            <input 
                                type="date" 
                                name="createdAt" 
                                value={quoteData.createdAt} 
                                onChange={handleChange} 
                                className="mt-1 w-full p-2 border rounded-md" 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                            <input 
                                type="date" 
                                name="expiresAt" 
                                value={quoteData.expiresAt} 
                                onChange={handleChange} 
                                className="mt-1 w-full p-2 border rounded-md" 
                            />
                        </div>
                    </div>

                    {/* r1-2c-qfm-notesstrip-a4c8f2d5 — collapsed Notes & Media
                        strip (mirrors QuoteDetailsView r1-1a-notes-strip-9b2e7c58):
                        header + count, expands to composer + existing-note list.
                        Attached to the pre-allocated (new) or real (edit) id.
                        allowFlagging off; visibility toggle is internal. */}
                    {notesQuoteId && (
                        <div className="mb-4">
                            <button
                                type="button"
                                onClick={() => setNotesExpanded(prev => !prev)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <MessageSquare size={16} /> Notes &amp; Media
                                    <span className="text-xs font-normal text-gray-400">({quoteNotes.length})</span>
                                </span>
                                {notesExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                            </button>
                            {notesExpanded && (
                                <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                                    <NoteComposer
                                        ref={noteComposerRef}
                                        attachedTo={[{ type: 'quote', id: notesQuoteId }]}
                                        orgId={orgId}
                                        currentUserProfile={currentUserProfile}
                                        allowFlagging={false}
                                        onDirtyChange={setComposerDirty}
                                        placeholder="Add a note or photo to this quote..."
                                    />
                                    <div className="space-y-2 pt-1">
                                        {quoteNotes.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                                        ) : (
                                            quoteNotes.map(note => ( // r1-2e-qfm-notecard-7b3e9d02
                                                <NoteCard key={note.id} note={note} currentUserProfile={currentUserProfile} orgId={orgId} />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── TEMPLATE PICKER ── only shown when creating new quote ── */}
                    {!editingQuote?.id && jobTemplates.length > 0 && (
                        <div className="mb-4">
                            {!showTemplatePicker ? (
                                <button
                                    type="button"
                                    onClick={() => setShowTemplatePicker(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 w-full border-2 border-dashed border-blue-200 rounded-xl text-sm text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all font-medium"
                                >
                                    <FileText size={15} className="flex-shrink-0" />
                                    Start from a template…
                                </button>
                            ) : (
                                <div className="border border-blue-200 rounded-xl overflow-hidden bg-blue-50">
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-blue-200">
                                        <span className="text-sm font-semibold text-blue-800">Choose a template</span>
                                        <button type="button" onClick={() => setShowTemplatePicker(false)} className="text-blue-400 hover:text-blue-600"><X size={16} /></button>
                                    </div>
                                    <div className="max-h-52 overflow-y-auto divide-y divide-blue-100">
                                        {jobTemplates.map(t => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => applyTemplate(t)}
                                                className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-blue-100 transition-colors"
                                            >
                                                <FileText size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-800 truncate">{t.name}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {t.lineItems?.length ? `${t.lineItems.length} line item${t.lineItems.length > 1 ? 's' : ''}` : 'No line items'}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <h3 className="text-lg font-semibold my-4">Line Items</h3>
                    <div className="space-y-4">
                        {quoteData.lineItems.map((item, index) => (
                            <div key={index} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm relative group mb-3">
                                
                                {/* Delete Button (Absolute) */}
                                <button 
                                    onClick={() => removeLineItem(index)} 
                                    className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
                                    
                                    {/* 1. Catalog Select (Full Width Mobile) */}
                                    <div className="col-span-12 md:col-span-3">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Catalog Item</label>
                                        <div className="relative">
                                            <Search size={14} className="absolute left-2.5 top-3 text-gray-400" />
                                            <select 
                                                value={item.serviceId || ''} 
                                                onChange={(e) => handleServiceSelect(index, e.target.value)} 
                                                className="w-full pl-8 p-2 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors outline-none focus:border-blue-500"
                                            >
                                                <option value="">Custom Entry</option>
                                                {pickerItems(services, item.serviceId).map(s => <option key={s.id} value={s.id}>{s.pickerLabel}</option>)/* catscreen-7a2c4e91 */}
                                            </select>
                                        </div>
                                    </div>

                                    {/* 2. Name & Description (Full Width Mobile) */}
                                    <div className="col-span-12 md:col-span-5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Item</label>
                                        <input 
                                            name="name" 
                                            value={item.name || ''} 
                                            onChange={(e) => handleLineItemChange(index, e)} 
                                            placeholder="Item Name (required)" 
                                            disabled={!!item.serviceId} 
                                            className={`w-full p-2 border rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none ${
                                                item.serviceId ? 'bg-gray-100 text-gray-600'
                                                : !item.name?.trim() ? 'border-red-300'
                                                : ''
                                            }`} 
                                        />
                                        <LineDescriptionInput
                                            name="description" 
                                            value={item.description || ''} 
                                            onChange={(e) => handleLineItemChange(index, e)} 
                                            labelClassName="text-[10px] font-bold text-gray-400 uppercase mb-1 block"
                                        />
                                    </div>

                                    {/* 3. Quantity (Half Width Mobile) */}
                                    <div className="col-span-6 md:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Qty</label>
                                        <input 
                                            type="number" 
                                            name="quantity" 
                                            value={item.quantity} 
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '' || parseFloat(v) <= 99999) handleLineItemChange(index, e);
                                            }}
                                            min="0" max="99999" step="0.01"
                                            className="w-full p-2 border rounded-lg text-center font-semibold text-gray-700" 
                                        />
                                    </div>

                                    {/* 4. Price (Half Width Mobile) */}
                                    <div className="col-span-6 md:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Price</label>
                                        <div className="relative">
                                            <span className="absolute left-2 top-2 text-gray-400 text-xs">$</span>
                                            <input 
                                                type="number" 
                                                name="price" 
                                                value={item.price} 
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === '' || parseFloat(v) <= 999999.99) handleLineItemChange(index, e);
                                                }}
                                                min="0" max="999999.99" step="0.01"
                                                className="w-full p-2 pl-5 border rounded-lg text-right font-semibold text-gray-700" 
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ROW 3: Options (Type, Selection, Catalog, Tax) */}
                                <div className="flex flex-wrap items-center gap-4 mt-3 pt-2 border-t border-gray-100">
                                    
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1 rounded ${item.type === 'Material' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                            {item.type === 'Material' ? <Package size={14} /> : <Wrench size={14} />}
                                        </div>
                                        <select 
                                            name="type" 
                                            value={item.type || 'Service'} 
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            className="text-xs border-0 bg-transparent font-medium text-gray-700 focus:ring-0 cursor-pointer hover:bg-gray-100 rounded px-1"
                                        >
                                            <option value="Service">Service</option>
                                            <option value="Material">Material</option>
                                        </select>
                                    </div>

                                    {/* PHASE 3 VERIFIER: r1-p3-qfm-selectionui-7c2b9e83
                                        Selection-type dropdown (Required / Recommended / Add-on / Included).
                                        Customer-facing semantics:
                                          Required    — no checkbox, always included
                                          Recommended — pre-checked checkbox, customer can uncheck
                                          Add-on      — unchecked checkbox, customer opts in
                                          Included    — descriptive sub-item nested under a priced parent
                                        Visual: icon prefix hints at the meaning at a glance. */}
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1 rounded ${
                                            item.selectionType === SELECTION_TYPES.RECOMMENDED ? 'bg-amber-100 text-amber-600' :
                                            item.selectionType === SELECTION_TYPES.ADDON       ? 'bg-emerald-100 text-emerald-600' :
                                            item.selectionType === SELECTION_TYPES.INCLUDED    ? 'bg-purple-100 text-purple-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {item.selectionType === SELECTION_TYPES.RECOMMENDED ? <Star size={14} /> :
                                             item.selectionType === SELECTION_TYPES.ADDON       ? <Plus size={14} /> :
                                             item.selectionType === SELECTION_TYPES.INCLUDED    ? <Link2 size={14} /> :
                                             <Lock size={14} />}
                                        </div>
                                        <select
                                            name="selectionType"
                                            value={item.selectionType || DEFAULT_SELECTION_TYPE}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            className="text-xs border-0 bg-transparent font-medium text-gray-700 focus:ring-0 cursor-pointer hover:bg-gray-100 rounded px-1"
                                            title="How this item appears to the customer when approving the quote"
                                        >
                                            <option value={SELECTION_TYPES.REQUIRED}>Required</option>
                                            <option value={SELECTION_TYPES.RECOMMENDED}>Recommended</option>
                                            <option value={SELECTION_TYPES.ADDON}>Add-on</option>
                                            <option value={SELECTION_TYPES.INCLUDED}>Included</option>
                                        </select>
                                    </div>

                                    {!item.serviceId && item.name && (
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="addToCatalog"
                                                id={`addToCatalog-${index}`}
                                                checked={item.addToCatalog}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                className="h-3 w-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                            />
                                            <label htmlFor={`addToCatalog-${index}`} className="ml-1 text-xs text-green-700 font-bold cursor-pointer flex items-center gap-1 select-none">
                                                <Save size={12} /> Save
                                            </label>
                                        </div>
                                    )}

                                    <div className="flex items-center ml-auto gap-2">
                                        {!item.taxExempt && (
                                            <div className="relative">
                                                <select
                                                    name="taxCodeId"
                                                    value={item.taxCodeId || ''}
                                                    onChange={(e) => handleLineItemChange(index, e)}
                                                    className="p-1 border rounded-md text-xs w-24 bg-white"
                                                    disabled={isLoadingTax}
                                                >
                                                    <option value="">Tax</option>
                                                    {taxOptions.map(code => (
                                                        <option key={code.Id} value={code.Id}>{code.Name}</option>
                                                    ))}
                                                </select>
                                                {isLoadingTax && (
                                                    <div className="absolute right-0 top-1 pointer-events-none">
                                                        <Loader2 size={10} className="animate-spin text-gray-400"/>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="taxExempt"
                                                id={`taxExempt-${index}`}
                                                checked={item.taxExempt}
                                                onChange={(e) => handleLineItemChange(index, e)}
                                                className="h-3 w-3 text-gray-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor={`taxExempt-${index}`} className="ml-1 text-xs text-gray-500 select-none">
                                                Exempt
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* PHASE 3 VERIFIER: r1-p3-qfm-parentpicker-1d4f8b62
                                    Conditional parent-picker row — only shown when
                                    this line is Included. Lists every OTHER line in
                                    the quote that isn't itself Included. Operator
                                    must pick a parent before saving (validated). */}
                                {item.selectionType === SELECTION_TYPES.INCLUDED && (
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-purple-100 bg-purple-50/40 -mx-4 -mb-4 px-4 pb-3 rounded-b-xl">
                                        <Link2 size={14} className="text-purple-500 flex-shrink-0" />
                                        <span className="text-xs font-medium text-purple-700 flex-shrink-0">Parent:</span>
                                        <select
                                            name="parentItemId"
                                            value={item.parentItemId || ''}
                                            onChange={(e) => handleLineItemChange(index, e)}
                                            className={`flex-1 p-1.5 border rounded-md text-xs bg-white ${!item.parentItemId ? 'border-red-300' : 'border-purple-200'}`}
                                        >
                                            <option value="">-- Choose a parent line --</option>
                                            {quoteData.lineItems
                                                .filter((other, otherIdx) =>
                                                    otherIdx !== index &&
                                                    other.itemId &&
                                                    other.selectionType !== SELECTION_TYPES.INCLUDED
                                                )
                                                .map(other => (
                                                    <option key={other.itemId} value={other.itemId}>
                                                        {other.name?.trim() || `(unnamed line)`}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <button onClick={addLineItem} className="mt-4 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800">
                        + Add Line Item
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div>
                            {/* doced-8f3a61c2 — label only: `notes` is staff-only (never on the PDF, email or portal); "Notes / Terms" read as customer-facing */}
                            <label className="block text-sm font-medium text-gray-700">Internal notes <span className="font-normal text-gray-400">(not shown to the customer)</span></label>
                            <textarea
                                name="notes"
                                value={quoteData.notes} 
                                onChange={handleChange} 
                                rows="4" 
                                className="mt-1 w-full p-2 border rounded-md focus:ring-1 focus:ring-blue-500 outline-none"
                            ></textarea>
                        </div>
                        <div className="text-right">
                            <div className="space-y-2 p-4 bg-gray-50 rounded-md border">
                                <p className="flex justify-between text-sm text-gray-600"><span>Subtotal:</span> <span>{formatCurrency(calculations.subtotal, currencyCode)}</span></p>
                                <p className="text-sm text-gray-500 flex justify-between"><span>Tax {calculations.subtotal > 0 ? `(${((calculations.taxAmount / calculations.subtotal) * 100).toFixed(1)}%)` : ''}:</span> <span>{formatCurrency(calculations.taxAmount, currencyCode)}</span></p>
                                <p className="flex justify-between text-lg font-bold border-t pt-2 mt-2"><span>Total:</span> <span>{formatCurrency(calculations.total, currencyCode)}</span></p>
                            </div>
                        </div>
                    </div>

                    {/* ── DEPOSIT ────────────────────────────────────────────────
                        SEAM 6 VERIFIER: r1-dep-qfm-ui-5c7e2b81
                        Writes deposit.type/value + depositRequired — the only two
                        deposit surfaces the client owns. Everything else
                        (amountResolved, depositStatus, paidAt) is written by
                        approveQuoteV2 and the deposit-paid webhook.
                        Sits by the totals because the preview reads off the total. */}
                    <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                            <Wallet size={16} className="text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-800">Deposit</h3>
                        </div>

                        {depositLocked ? (
                            /* Post-approval the amount is what Checkout charges — show it, don't offer edits. */
                            <div className="space-y-1">
                                <p className="text-sm text-gray-600">
                                    {quoteData.deposit?.amountResolved != null ? (
                                        <>Deposit of <strong className="text-gray-900">{formatCurrency(quoteData.deposit.amountResolved, currencyCode)}</strong> — </>
                                    ) : 'Deposit — '}
                                    {/* 1c-qfm-label-7c9d1a2e — Seam 1c: 'waived' reads as a
                                        neutral (gray) settled state, never green "paid". */}
                                    <span className={`font-semibold ${
                                        quoteData.depositStatus === 'paid' || quoteData.depositStatus === 'recorded'
                                            ? 'text-green-700'
                                            : quoteData.depositStatus === 'refunded' || quoteData.depositStatus === 'waived' ? 'text-gray-700' : 'text-amber-700'
                                    }`}>
                                        {quoteData.depositStatus === 'paid'     ? 'paid'
                                         : quoteData.depositStatus === 'recorded' ? 'recorded'
                                         : quoteData.depositStatus === 'refunded' ? 'refunded'
                                         : quoteData.depositStatus === 'waived'   ? 'waived'
                                         : 'unpaid'}
                                    </span>
                                </p>
                                <p className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Lock size={11} className="flex-shrink-0" />
                                    Set when this {docWordLower} was approved — it can't be changed here.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* SEAM 1d VERIFIER: qfm-offline-note-9c3a5f21
                                    Neutral offline-collection note. Shown only when the org
                                    has no Stripe AND a deposit is on. No Stripe link, no
                                    "connect" language — some orgs collect every deposit
                                    offline by choice, permanently. */}
                                {!stripeReady && depositEnabled && (
                                    <div className="mb-3 flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-xs">
                                        <AlertCircle size={14} className="shrink-0 mt-0.5 text-gray-400" />
                                        <p>The customer won't see an online payment button for this {docWordLower}. Arrange the deposit with them directly — by cheque, cash, e-transfer, or your own terminal — and record it on the {docWordLower} once you've received it.</p>
                                    </div>
                                )}
                                <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                                    <input
                                        type="checkbox"
                                        checked={depositEnabled}
                                        onChange={(e) => setDepositEnabled(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Collect a deposit on this {docWordLower}</span>
                                </label>

                                {depositEnabled && (
                                    <div className="mt-3 pl-6 space-y-3">
                                        <div className="flex flex-wrap items-end gap-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Type</label>
                                                <select
                                                    value={depositType}
                                                    onChange={(e) => setDepositType(e.target.value)}
                                                    className="p-2 border rounded-md text-sm bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                                                >
                                                    <option value={DEPOSIT_TYPES.PERCENTAGE}>Percentage</option>
                                                    <option value={DEPOSIT_TYPES.FIXED}>Fixed amount</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                                                    {depositType === DEPOSIT_TYPES.PERCENTAGE ? 'Percent of total' : 'Amount'}
                                                </label>
                                                <div className="relative">
                                                    {depositType === DEPOSIT_TYPES.FIXED && (
                                                        <span className="absolute left-2 top-2.5 text-gray-400 text-xs">$</span>
                                                    )}
                                                    <input
                                                        type="number"
                                                        value={depositValue}
                                                        onChange={(e) => setDepositValue(e.target.value)}
                                                        min="0"
                                                        max={depositType === DEPOSIT_TYPES.PERCENTAGE ? '100' : '999999.99'}
                                                        step="any" // depdraft-3c8e5a14 — arrows move 1 (1% / $1); any typed decimal stays valid
                                                        placeholder={depositType === DEPOSIT_TYPES.PERCENTAGE ? '25' : '500.00'}
                                                        className={`w-32 p-2 ${depositType === DEPOSIT_TYPES.FIXED ? 'pl-5' : 'pr-7'} border rounded-md text-sm text-right font-semibold disabled:opacity-60 disabled:cursor-not-allowed`}
                                                    />
                                                    {depositType === DEPOSIT_TYPES.PERCENTAGE && (
                                                        <span className="absolute right-2 top-2.5 text-gray-400 text-xs">%</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="pb-2 text-sm text-gray-600">
                                                {depositPreview > 0 ? (
                                                    <>
                                                        ≈ <strong className="text-gray-900">{formatCurrency(depositPreview, currencyCode)}</strong>{' '}
                                                        <span className="text-xs text-gray-400">on the current total</span>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Enter an amount to preview the deposit.</span>
                                                )}
                                            </div>
                                        </div>
                                        {/* depdraft-3c8e5a14 — a Draft can be saved like this; sending can't. */}
                                        {!(Number(depositValue) > 0) && (
                                            <p className="text-xs font-medium text-amber-700">Deposit amount needed — you can save this as a draft, but enter the amount before sending.</p>
                                        )}

                                        <label className="flex items-start gap-2 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={depositRequired}
                                                onChange={(e) => setDepositRequired(e.target.checked)}
                                                className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Require the deposit before this {docWordLower} is approved
                                                <span className="block text-xs text-gray-500 mt-0.5">
                                                    {depositRequired
                                                        ? `The customer signs, then pays. The ${docWordLower} waits in "Awaiting Deposit" and jobs aren't scheduled until the payment clears.`
                                                        : `The ${docWordLower} is approved as soon as the customer signs. The deposit is requested afterwards.`}
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center p-6 pt-4 border-t mt-4 gap-4">
                    <div className="w-full md:w-auto">
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2 sm:gap-3">
                        <button onClick={handleGuardedClose} className="order-3 sm:order-1 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                        <button onClick={() => handleSave(false)} disabled={isSaving} className="order-2 sm:order-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                            {isSaving ? 'Saving...' : 'Save & Close'}
                        </button>
                        <button onClick={() => handleSave(true)} disabled={isSaving} className="order-1 sm:order-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
                            {isSaving ? 'Sending...' : 'Save & Send'}
                        </button>
                    </div>
                </div>

                {/* seamF2-modal-8d5e2b6c — active confirm before an optional-deposit
                    approval. Cancel closes it and leaves the estimate unsaved; confirm
                    re-runs the save with the confirm skipped (sendEmail is always false
                    on this path — Send forces status 'Sent', which never triggers this). */}
                {depositApprovalConfirm && (
                    <ConfirmationModal
                        isOpen={true}
                        onClose={() => setDepositApprovalConfirm(null)}
                        onConfirm={() => {
                            setDepositApprovalConfirm(null);
                            handleSave(false, true);
                        }}
                        title="Approve and mark the deposit as owing?"
                        message={`This ${docWordLower} has a ${formatCurrency(depositApprovalConfirm.amount, currencyCode)} deposit. Approving marks it as owing — you can record a payment or waive it afterward.`}
                        confirmText="Approve & mark owing"
                    />
                )}
            </div>
        </div>
    );
};

export default QuoteFormModal;